import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';

const LOCALES: Record<string, string> = {
    uk: '🇺🇦 Ukrainian / Українська',
    ru: '🇷🇺 Russian / Русский',
    hi: '🇮🇳 Hindi / हिन्दी',
    es: '🇪🇸 Spanish / Español',
    pt: '🇧🇷 Portuguese / Português',
    tr: '🇹🇷 Turkish / Türkçe',
    ko: '🇰🇷 Korean / 한국어',
    vi: '🇻🇳 Vietnamese / Tiếng Việt',
    de: '🇩🇪 German / Deutsch',
    fr: '🇫🇷 French / Français',
    zh: '🇨🇳 Chinese / 中文',
    ja: '🇯🇵 Japanese / 日本語',
    ar: '🇸🇦 Arabic / العربية',
};

export default class AddTranslationModal extends Modal {
    locale: string = '';
    content: string = '';
    loading: boolean = false;
    existingTranslations: any[] = [];
    loadingExisting: boolean = true;

    oninit(vnode: any) {
        super.oninit(vnode);
        this.loadExisting();
    }

    async loadExisting() {
        this.loadingExisting = true;
        try {
            const response = await app.request({
                method: 'GET',
                url: app.forum.attribute('apiUrl') + '/post-translations?post_id=' + this.attrs.post.id(),
            });
            this.existingTranslations = response.data || [];
            // If editing and a locale is pre-selected
            if (this.existingTranslations.length > 0 && !this.locale) {
                // Don't pre-select, let user choose
            }
        } catch (e) {
            console.error('Failed to load translations:', e);
        }
        this.loadingExisting = false;
        m.redraw();
    }

    className() {
        return 'TranslationModal Modal--large';
    }

    title() {
        return app.translator.trans('predictiontalk-post-translations.forum.modal.title');
    }

    content() {
        const existingLocales = this.existingTranslations.map((t: any) => t.attributes.locale);

        return (
            <div className="Modal-body">
                <div className="Form">
                    <div className="Form-group">
                        <label>{app.translator.trans('predictiontalk-post-translations.forum.modal.locale_label')}</label>
                        <select
                            value={this.locale}
                            onchange={(e: Event) => {
                                this.locale = (e.target as HTMLSelectElement).value;
                                // Load existing translation content if editing
                                const existing = this.existingTranslations.find(
                                    (t: any) => t.attributes.locale === this.locale
                                );
                                if (existing) {
                                    // We can't easily get raw markdown from XML, so show a note
                                    this.content = '';
                                }
                            }}
                        >
                            <option value="">{app.translator.trans('predictiontalk-post-translations.forum.modal.locale_placeholder')}</option>
                            {Object.entries(LOCALES).map(([code, name]) => (
                                <option value={code}>
                                    {name} {existingLocales.includes(code) ? '✓' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {this.locale && existingLocales.includes(this.locale) && (
                        <div className="Form-group">
                            <p style="color: #F59E0B; font-size: 13px;">
                                <i className="fas fa-info-circle" /> A translation for this language already exists. Submitting will overwrite it.
                            </p>
                        </div>
                    )}

                    <div className="Form-group">
                        <label>{app.translator.trans('predictiontalk-post-translations.forum.modal.content_label')}</label>
                        <textarea
                            className="FormControl"
                            rows={15}
                            value={this.content}
                            placeholder={app.translator.trans('predictiontalk-post-translations.forum.modal.content_placeholder') as string}
                            oninput={(e: Event) => { this.content = (e.target as HTMLTextAreaElement).value; }}
                        />
                    </div>

                    <div className="Form-group">
                        <Button
                            className="Button Button--primary"
                            loading={this.loading}
                            disabled={!this.locale || !this.content.trim()}
                            onclick={() => this.submit()}
                        >
                            {app.translator.trans('predictiontalk-post-translations.forum.modal.submit')}
                        </Button>

                        {this.locale && existingLocales.includes(this.locale) && (
                            <Button
                                className="Button Button--danger"
                                style="margin-left: 8px;"
                                onclick={() => this.deleteTranslation()}
                            >
                                {app.translator.trans('predictiontalk-post-translations.forum.modal.delete')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    async submit() {
        this.loading = true;
        m.redraw();

        try {
            await app.request({
                method: 'POST',
                url: app.forum.attribute('apiUrl') + '/post-translations',
                body: {
                    data: {
                        type: 'post-translations',
                        attributes: {
                            postId: this.attrs.post.id(),
                            locale: this.locale,
                            content: this.content,
                        },
                    },
                },
            });

            app.alerts.show({ type: 'success' },
                app.translator.trans('predictiontalk-post-translations.forum.post.translation_saved')
            );

            // Reload the page to see the translation
            window.location.reload();
        } catch (e: any) {
            console.error('Failed to save translation:', e);
            if (e.alert) {
                // Flarum handles this
            }
        }

        this.loading = false;
        m.redraw();
    }

    async deleteTranslation() {
        if (!confirm(app.translator.trans('predictiontalk-post-translations.forum.modal.delete_confirm') as string)) {
            return;
        }

        const existing = this.existingTranslations.find(
            (t: any) => t.attributes.locale === this.locale
        );

        if (!existing) return;

        try {
            await app.request({
                method: 'DELETE',
                url: app.forum.attribute('apiUrl') + '/post-translations/' + existing.id,
            });

            window.location.reload();
        } catch (e) {
            console.error('Failed to delete translation:', e);
        }
    }
}
